import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Row, Col, InputNumber, Switch, Spin, App } from "antd";
import { Line } from "@ant-design/plots";
import dayjs from "dayjs";
import GenericCard from "components/UI/GenericCard/GenericCard";
import DashedTooltip from "components/UI/DashedTooltip";
import AntTable from "components/UI/AntTable";
import ExportCsvButton from "components/UI/ExportCsvButton";
import { returnUpDownClassname } from "utils/helpers";
import { apiSimulateOptionsPortfolio } from "api/apiPredictions";

// Portfolio Simulation is a real backtest of the strategy: Signal
// Parameters above decide which underlying moves count as a "buy signal";
// this card answers "did those signals actually make good money on REAL
// option prices, and was the portfolio profitable at the end." Every
// complete signal is a trade candidate regardless of whether it hit the
// SIGNAL's own target% -- every trade here exits at ITS OWN take-profit %
// or the contract's real expiration, whichever comes first (never an
// arbitrary bar cutoff). The whole simulation runs server-side (see
// api/predictions/options-portfolio-simulator.js) against the historical
// option chain in ClickHouse -- this card only sends params and renders the
// result.
const EMPTY_RESULT = {
  finalEquity: 0,
  totalReturnPercent: 0,
  maxDrawdownPercent: 0,
  tradesTaken: 0,
  tradesSkipped: 0,
  equityCurve: [],
  tradeLog: [],
  tradeStats: { successRate: 0, avgReturnPercent: 0 },
};

const PortfolioSimulationCard = (props) => {
  const { optionsResults, signalRequest } = props;
  const { message } = App.useApp();

  const [startingCapital, setStartingCapital] = useState(10000);
  const [allocationPercent, setAllocationPercent] = useState(10);
  const [maxConcurrentPositions, setMaxConcurrentPositions] = useState(5);
  const [commissionPerTrade, setCommissionPerTrade] = useState(0);

  const [takeProfitPercent, setTakeProfitPercent] = useState(
    optionsResults.params.targetGainPercent,
  );
  // Off (0) by default -- the real chain's coverage is sparse enough
  // (gaps, monthly-only in spots) that a tight bars window skips almost
  // every signal. Only turn these on if you specifically want to exclude
  // near-0DTE or far-dated contracts.
  const [minBars, setMinBars] = useState(0);
  const [maxDteBars, setMaxDteBars] = useState(0);

  // When the nearest real expiry is closer than Min bars, skip that trade by
  // default -- too close to realistically hold. Toggle on to roll forward to
  // the next listed expiration instead of skipping.
  const [rollToNextExpiryEnabled, setRollToNextExpiryEnabled] = useState(false);

  const [result, setResult] = useState(EMPTY_RESULT);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      !(startingCapital > 0) ||
      !(allocationPercent > 0) ||
      !(maxConcurrentPositions >= 1) ||
      !(takeProfitPercent > 0)
    ) {
      return undefined;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsLoading(true);
      try {
        const data = await apiSimulateOptionsPortfolio(
          {
            ...signalRequest,
            startingCapital,
            allocationPercent,
            maxConcurrentPositions,
            commissionPerTrade,
            takeProfitPercent,
            minBars,
            maxDteBars,
            rollToNextExpiryEnabled,
          },
          controller.signal,
        );
        setResult(data);
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          message.error(
            err?.response?.data?.message ||
              "Failed to run portfolio simulation",
          );
        }
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [
    signalRequest,
    startingCapital,
    allocationPercent,
    maxConcurrentPositions,
    commissionPerTrade,
    takeProfitPercent,
    minBars,
    maxDteBars,
    rollToNextExpiryEnabled,
    message,
  ]);

  const sortByNumber = useCallback((a, b) => {
    if (a === b) return 0;
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return a > b ? 1 : -1;
  }, []);

  const tradeLogColumns = [
    {
      title: "Entry Date",
      dataIndex: "entryDate",
      render: (text) => (
        <>
          {dayjs(text).format("DD/MM/YY")}{" "}
          <span className="text-xs">({dayjs(text).format("ddd")})</span>
        </>
      ),
      sorter: { compare: (a, b) => sortByNumber(a.entryDate, b.entryDate) },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, obj) => (
        <span className={text === "taken" ? "green" : "yellow"}>
          {text === "taken" ? "Taken" : `Skipped (${obj.skipReason})`}
        </span>
      ),
      filterMultiple: false,
      filters: [
        { text: "Taken", value: "taken" },
        { text: "Skipped", value: "skipped" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Underlying Move",
      dataIndex: "underlyingMovePercent",
      render: (text, obj) =>
        text != null ? (
          <span className={returnUpDownClassname(text)}>
            {text}%{" "}
            <span className="text-xs">
              (${obj.underlyingEntryPrice} → ${obj.underlyingExitPrice})
            </span>
          </span>
        ) : (
          "-"
        ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.underlyingMovePercent, b.underlyingMovePercent),
      },
    },
    {
      title: "Strike",
      dataIndex: "strike",
      render: (text) => (text != null ? `$${text}` : "-"),
      sorter: { compare: (a, b) => sortByNumber(a.strike, b.strike) },
    },
    {
      title: "Expiration",
      dataIndex: "expiration",
      render: (text) => (text ? dayjs(text).format("DD/MM/YY") : "-"),
      sorter: { compare: (a, b) => sortByNumber(a.expiration, b.expiration) },
    },
    {
      title: "Exit Date",
      dataIndex: "exitDate",
      render: (text, obj) =>
        text ? (
          <>
            {dayjs(text).format("DD/MM/YY")}{" "}
            <span className="text-xs">
              ({dayjs(text).format("ddd")}
              {obj.exitReason ? `, ${obj.exitReason}` : ""})
            </span>
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Days Held",
      dataIndex: "daysHeld",
      render: (text) => text ?? "-",
      sorter: { compare: (a, b) => sortByNumber(a.daysHeld, b.daysHeld) },
    },
    {
      title: "Entry Option $",
      dataIndex: "entryOptionPrice",
      render: (text) => (text != null ? `$${text}` : "-"),
      sorter: {
        compare: (a, b) => sortByNumber(a.entryOptionPrice, b.entryOptionPrice),
      },
    },
    {
      title: "Exit Option $",
      dataIndex: "exitOptionPrice",
      render: (text) => (text != null ? `$${text}` : "-"),
      sorter: {
        compare: (a, b) => sortByNumber(a.exitOptionPrice, b.exitOptionPrice),
      },
    },
    {
      title: "Allocated $",
      dataIndex: "allocatedCapital",
      render: (text) => (text != null ? `$${text.toLocaleString()}` : "-"),
      sorter: {
        compare: (a, b) => sortByNumber(a.allocatedCapital, b.allocatedCapital),
      },
    },
    {
      title: "Option Return",
      dataIndex: "returnPercent",
      render: (text) =>
        text != null ? (
          <span className={returnUpDownClassname(text)}>{text}%</span>
        ) : (
          "-"
        ),
      sorter: {
        compare: (a, b) => sortByNumber(a.returnPercent, b.returnPercent),
      },
    },
    {
      title: "PnL $",
      dataIndex: "pnl",
      render: (text) =>
        text != null ? (
          <span className={returnUpDownClassname(text)}>
            ${text.toLocaleString()}
          </span>
        ) : (
          "-"
        ),
      sorter: { compare: (a, b) => sortByNumber(a.pnl, b.pnl) },
    },
    {
      title: "Portfolio Impact",
      dataIndex: "portfolioContributionPercent",
      render: (text) =>
        text != null ? (
          <span className={returnUpDownClassname(text)}>{text}%</span>
        ) : (
          "-"
        ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(
            a.portfolioContributionPercent,
            b.portfolioContributionPercent,
          ),
      },
    },
    {
      title: "Equity After",
      dataIndex: "equityAfter",
      render: (text) => (text != null ? `$${text.toLocaleString()}` : "-"),
      sorter: {
        compare: (a, b) => sortByNumber(a.equityAfter, b.equityAfter),
      },
    },
  ];

  return (
    <GenericCard
      title="Portfolio Simulation"
      tooltipText="A real backtest of the strategy: EVERY complete signal is a trade candidate, and each one exits at its own Take profit % or the contract's real expiration -- whichever comes first. Opens a position sized as a % of current equity (if capital/room allows), closes it at that exit, and compounds. Equity is sampled at each signal event and open positions are valued at cost, not repriced daily -- a screening tool for 'how would my capital have grown,' not a precise account statement."
      maxHeight={700}
      className="table-card"
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={12} sm={4}>
            <Form.Item
              label="Take profit (%)"
              tooltip="Exit the moment the underlying's close clears this move; otherwise hold until the contract's real expiration. Defaults to the Target gain % in Signal Parameters above (freely editable)."
            >
              <InputNumber
                min={0.1}
                step={0.5}
                style={{ width: "100%" }}
                value={takeProfitPercent}
                onChange={setTakeProfitPercent}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label="Min bars"
              tooltip="Skip a signal whose ACTUAL matched contract is closer than this many trading bars out -- too close to realistically trade. 0 (default) = no floor -- the real chain is sparse enough that a tight floor can skip almost everything."
            >
              <InputNumber
                min={0}
                step={1}
                style={{ width: "100%" }}
                value={minBars}
                onChange={setMinBars}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label="Max DTE (bars)"
              tooltip="Skip a signal whose ACTUAL matched contract expires FURTHER than this many trading bars out -- too much time value to pay for. 0 (default) = no ceiling. Checked against whichever contract actually gets used (the rolled-forward one, if Min bars forced a roll)."
            >
              <InputNumber
                min={0}
                step={1}
                style={{ width: "100%" }}
                value={maxDteBars}
                onChange={setMaxDteBars}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label="Roll too-close expiries"
              tooltip="When the nearest real expiry is closer than Min bars, the default is to skip that trade -- too close to realistically hold. Enable to roll forward to the next listed expiration instead of skipping."
            >
              <Switch
                checked={rollToNextExpiryEnabled}
                onChange={setRollToNextExpiryEnabled}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item label="Starting capital ($)">
              <InputNumber
                min={100}
                step={1000}
                style={{ width: "100%" }}
                value={startingCapital}
                onChange={setStartingCapital}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label="Allocation per trade (%)"
              tooltip="% of CURRENT equity risked on each new position -- compounds as the portfolio grows or shrinks."
            >
              <InputNumber
                min={1}
                max={100}
                step={1}
                style={{ width: "100%" }}
                value={allocationPercent}
                onChange={setAllocationPercent}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label="Max concurrent positions"
              tooltip="Cap on simultaneously open positions -- a new signal is skipped if this many are already open, even with capital available."
            >
              <InputNumber
                min={1}
                step={1}
                style={{ width: "100%" }}
                value={maxConcurrentPositions}
                onChange={setMaxConcurrentPositions}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item
              label="Commission per leg ($)"
              tooltip="Flat $ charged on both the entry and the exit of every position taken."
            >
              <InputNumber
                min={0}
                step={0.5}
                style={{ width: "100%" }}
                value={commissionPerTrade}
                onChange={setCommissionPerTrade}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Spin spinning={isLoading} tip="Running simulation...">
        <div className="flex gap-8 flex-wrap pb-4">
          <div>
            <h5>Final equity</h5>
            <h2 className={returnUpDownClassname(result.totalReturnPercent)}>
              ${result.finalEquity.toLocaleString()}
            </h2>
            <p className="text-xs">
              {result.totalReturnPercent > 0 ? "+" : ""}
              {result.totalReturnPercent}% total return
            </p>
          </div>
          <div>
            <DashedTooltip text="Worst peak-to-trough decline in this equity curve -- the deepest hole this portfolio sizing would have gone through.">
              <h5>Max drawdown</h5>
            </DashedTooltip>
            <h3 className="red">{result.maxDrawdownPercent}%</h3>
          </div>
          <div>
            <DashedTooltip text="Signals skipped because capital or the max-concurrent-positions cap didn't allow taking them, or because no tradable option existed for that signal -- see the trade log below for each one's reason.">
              <h5>Trades taken / skipped</h5>
            </DashedTooltip>
            <h3>
              {result.tradesTaken} / {result.tradesSkipped}
            </h3>
          </div>
          <div>
            <DashedTooltip text="% of TAKEN trades that closed with a positive option return -- sizing-independent, purely about how often this exit rule wins.">
              <h5>Trade success rate</h5>
            </DashedTooltip>
            <h3>{result.tradeStats.successRate}%</h3>
          </div>
          <div>
            <DashedTooltip text="Average option return % across TAKEN trades -- sizing-independent, the raw edge of this exit rule before position sizing/compounding.">
              <h5>Avg option gain</h5>
            </DashedTooltip>
            <h3
              className={returnUpDownClassname(
                result.tradeStats.avgReturnPercent,
              )}
            >
              {result.tradeStats.avgReturnPercent}%
            </h3>
          </div>
        </div>

        {result.equityCurve.length > 0 && (
          <Line
            data={result.equityCurve}
            xField="date"
            yField="equity"
            smooth={false}
            lineStyle={{ lineWidth: 2 }}
            height={350}
            autoFit={false}
          />
        )}

        {result.tradeLog.length > 0 && (
          <div className="pt-4">
            <h5>Trade log</h5>
            <p className="text-xs pb-2">
              Every signal the simulation evaluated, in order -- how much
              capital each taken trade got, what it returned, how much it alone
              moved total portfolio equity (Portfolio Impact), and the equity
              right after it closed.
            </p>
            <AntTable
              rowKey={(row, index) => `${row.entryDate}-${index}`}
              dataSource={result.tradeLog}
              columns={tradeLogColumns}
            />
            <div className="pt-4">
              <ExportCsvButton
                data={result.tradeLog}
                fileName="portfolio-simulation-trades"
              />
            </div>
          </div>
        )}
      </Spin>
    </GenericCard>
  );
};

PortfolioSimulationCard.propTypes = {
  optionsResults: PropTypes.object,
  signalRequest: PropTypes.object.isRequired,
};

export default PortfolioSimulationCard;
